import { useMutation, useQuery, useQueryClient } from 'react-query';
import { messagesAPI } from '../apiClient';
import {
  MESSAGE_THREADS_KEY,
  MESSAGE_UNREAD_KEY,
  messageThreadForTechKey,
  messageThreadKey,
  messageThreadMessagesKey,
} from './queryKeys';

export function useUnreadMessageCount(options = {}) {
  return useQuery(
    [MESSAGE_UNREAD_KEY],
    () => messagesAPI.getUnreadCount(),
    {
      refetchInterval: 60000,
      ...options,
    }
  );
}

export function useMessageThreads(options = {}) {
  return useQuery(
    [MESSAGE_THREADS_KEY],
    () => messagesAPI.getThreads(),
    {
      refetchInterval: 60000,
      select: (response) => response.data?.threads || [],
      ...options,
    }
  );
}

export function useEnsureMessageThread(technicianId, { enabled = true } = {}) {
  return useQuery(
    messageThreadForTechKey(technicianId),
    async () => {
      const response = await messagesAPI.createThread({ technicianId });
      return response.data?.thread;
    },
    {
      enabled: enabled && !!technicianId,
      staleTime: 300000,
    }
  );
}

export function useMessageThreadConversation(threadId, {
  enabled = true,
  pollInterval = null,
  liveUpdates = true,
  markReadOnLoad = true,
} = {}) {
  const queryClient = useQueryClient();
  const effectivePollInterval = pollInterval ?? (liveUpdates ? 60000 : 15000);

  const threadQuery = useQuery(
    messageThreadKey(threadId),
    () => messagesAPI.getThread(threadId),
    {
      enabled: enabled && !!threadId,
      refetchInterval: effectivePollInterval,
      select: (response) => response.data?.thread,
    }
  );

  const messagesQuery = useQuery(
    messageThreadMessagesKey(threadId),
    () => messagesAPI.getMessages(threadId, { limit: 100 }),
    {
      enabled: enabled && !!threadId,
      refetchInterval: effectivePollInterval,
      select: (response) => response.data?.messages || [],
    }
  );

  const invalidateConversation = async () => {
    await Promise.all([
      queryClient.invalidateQueries(messageThreadKey(threadId)),
      queryClient.invalidateQueries(messageThreadMessagesKey(threadId)),
      queryClient.invalidateQueries([MESSAGE_THREADS_KEY]),
      queryClient.invalidateQueries([MESSAGE_UNREAD_KEY]),
    ]);
  };

  const markReadMutation = useMutation(
    () => messagesAPI.markThreadRead(threadId),
    {
      onSuccess: invalidateConversation,
    }
  );

  const sendMutation = useMutation(
    ({ body, serviceCallId = null }) => messagesAPI.sendMessage(threadId, { body, serviceCallId }),
    {
      onSuccess: invalidateConversation,
    }
  );

  const markRead = markReadMutation.mutateAsync;

  return {
    thread: threadQuery.data,
    messages: messagesQuery.data || [],
    isLoading: threadQuery.isLoading || messagesQuery.isLoading,
    isSending: sendMutation.isLoading,
    sendMessage: sendMutation.mutateAsync,
    markRead,
    markReadOnLoad,
    isMarkingRead: markReadMutation.isLoading,
  };
}
